// Liste filtrée, paginée, tenue à jour en temps réel (porté de FeedbackList
// et feedbacks.realtime). Le serveur n'envoie que le type et l'identifiant
// d'un événement : la liste relit le retour avec les droits du lecteur.
import type { JSX } from 'preact'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import type { FeedbackItem } from '@feedbaacks/sdk'
import { FeedbaacksError } from '@feedbaacks/sdk'
import { DEFAULT_FILTERS, useFeedbaacks, type Filters } from '../context'
import { Button } from '../primitives'
import { useSelector } from '../store'
import { errorMessage, filtersToQuery } from '../util'
import { EmptyFiltered, EmptyFirst } from './parts'
import { Row, RowSkeleton } from './Row'

const PAGE_SIZE = 30

export function isDefaultView(f: Filters): boolean {
  return (Object.keys(DEFAULT_FILTERS) as Array<keyof Filters>).every((k) => (k === 'search' ? !f.search.trim() : f[k] === DEFAULT_FILTERS[k]))
}

/** Remplace l'élément, ou l'ajoute en tête s'il est nouveau (fonction pure). */
export function upsert(items: FeedbackItem[], next: FeedbackItem, prependIfMissing: boolean): FeedbackItem[] {
  let found = false
  const out = items.map((it) => {
    if (it.id !== next.id) return it
    found = true
    return next
  })
  if (found) return out
  return prependIfMissing ? [next, ...items] : items
}

/** L'élément quitte-t-il la vue courante (archives, corbeille) ? */
export function leavesView(item: FeedbackItem, f: Filters): boolean {
  return (f.archived === 'active' && item.is_archived) || (f.archived === 'archived' && !item.is_archived)
}

export function List(): JSX.Element {
  const { client, store, t, realtime, changes } = useFeedbaacks()
  const filters = useSelector(store, (s) => s.filters)
  const selectedId = useSelector(store, (s) => s.selectedId)
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const request = useRef(0)
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const rememberAuthors = useCallback((list: FeedbackItem[]) => {
    const known = new Map(store.get().knownAuthors.map((a) => [a.id, a]))
    let changed = false
    for (const it of list) {
      if (it.author?.id && !known.has(it.author.id)) {
        known.set(it.author.id, { id: it.author.id, name: it.author.name })
        changed = true
      }
    }
    if (changed) store.set({ knownAuthors: [...known.values()] })
  }, [store])

  const loadFirst = useCallback(async () => {
    const id = ++request.current
    setLoading(true)
    setError(null)
    try {
      const page = await client.feedback.list(filtersToQuery(filters, 0, PAGE_SIZE))
      if (id !== request.current) return
      setItems(page.data)
      setHasMore(page.has_more)
      rememberAuthors(page.data)
    } catch (err) {
      if (id === request.current) setError(errorMessage(err, t('load_error')))
    } finally {
      if (id === request.current) setLoading(false)
    }
  }, [client, filters, rememberAuthors, t])
  useEffect(() => {
    void loadFirst()
  }, [loadFirst])

  const loadMore = async () => {
    if (!hasMore || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await client.feedback.list(filtersToQuery(filters, items.length, PAGE_SIZE))
      setItems((prev) => [...prev, ...page.data.filter((n) => !prev.some((p) => p.id === n.id))])
      setHasMore(page.has_more)
      rememberAuthors(page.data)
    } catch {
      // le bouton reste : nouvel essai possible
    } finally {
      setLoadingMore(false)
    }
  }

  const change = useCallback((next: FeedbackItem) => {
    setItems((prev) => (leavesView(next, filtersRef.current) ? prev.filter((it) => it.id !== next.id) : upsert(prev, next, false)))
  }, [])
  const remove = useCallback((id: string) => setItems((prev) => prev.filter((it) => it.id !== id)), [])

  // Modifications faites ailleurs dans l'instance (formulaire, aperçu).
  useEffect(() => changes.on((c) => {
    if (c.removedId) remove(c.removedId)
    if (c.item) setItems((prev) => upsert(prev, c.item!, true))
  }), [changes, remove])

  // Temps réel : relire le retour concerné, avec les droits du lecteur.
  useEffect(() => realtime.on((e) => {
    if (!e.item_id) return
    if (e.data?.deleted) {
      remove(e.item_id)
      return
    }
    client.feedback.get(e.item_id).then(
      (item) => setItems((prev) => (leavesView(item, filtersRef.current) ? prev.filter((it) => it.id !== item.id) : upsert(prev, item, e.type === 'feedback.item_created' && isDefaultView(filtersRef.current)))),
      (err: unknown) => {
        if (err instanceof FeedbaacksError && err.status === 404) remove(e.item_id)
      },
    )
  }), [realtime, client, remove])

  // Élément sélectionné hors de la page chargée (lien profond, suggestion).
  useEffect(() => {
    if (!selectedId || loading || items.some((it) => it.id === selectedId)) return
    client.feedback.get(selectedId).then((it) => setItems((prev) => upsert(prev, it, true)), () => {})
  }, [selectedId, loading, items, client])

  const toggle = useCallback((id: string) => {
    const open = store.get().selectedId === id
    store.set({ selectedId: open ? null : id })
    if (open) return
    setItems((prev) => prev.map((it) => (it.id === id && (it.is_unread || it.unread_comments_count > 0) ? { ...it, is_unread: false, unread_comments_count: 0 } : it)))
    void client.feedback.markRead(id).catch(() => {})
  }, [store, client])

  if (loading) {
    return (
      <div data-testid="feedbaacks-list-loading">
        {Array.from({ length: 6 }, (_, i) => <RowSkeleton key={i} />)}
      </div>
    )
  }
  if (error) {
    return (
      <div class="fb-empty" role="alert">
        <p class="fb-muted">{error}</p>
        <Button onClick={() => void loadFirst()}>{t('retry')}</Button>
      </div>
    )
  }
  if (!items.length) {
    return isDefaultView(filters) ? (
      <EmptyFirst onCreate={(type) => store.set({ composerOpen: true, pendingType: type, editingId: null })} />
    ) : (
      <EmptyFiltered onClear={() => store.set({ filters: DEFAULT_FILTERS })} />
    )
  }
  return (
    <div class="fb-list" data-testid="feedbaacks-list">
      {items.map((it) => (
        <Row key={it.id} item={it} expanded={selectedId === it.id} onToggle={toggle} onChange={change} onRemove={remove} />
      ))}
      {hasMore && (
        <div class="fb-load-more">
          <Button variant="ghost" onClick={() => void loadMore()} disabled={loadingMore} testId="feedbaacks-load-more">
            {loadingMore ? t('loading') : t('load_more')}
          </Button>
        </div>
      )}
    </div>
  )
}
